import React, { useCallback, useMemo } from 'react';
import { Button, Tooltip, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import {
  CREATE_REQUESTS,
  CREATE_REQUESTS_TOOLTIP,
  NO_COMPANIES_FOUND,
  NO_COMPANY_USERS,
  NO_MEASUREMENT_DATE_SET,
  REQUEST_ALREADY_EXISTS,
  SELECT_ALL_COMPANIES,
  SEND_REQUESTS,
  SEND_REQUESTS_TOOLTIP,
  UNSELECT_ALL_COMPANIES,
} from 'common/constants/firms';
import PerfectScrollbar from 'components/SafePerfectScrollbar';
import { FRModalContentProps } from './FRModalContent.props';
import InvalidCompaniesBox from '../InvalidCompaniesBox/InvalidCompaniesBox';
import ModalItemCompanyBox from '../ModalItemCompanyBox/ModalItemCompanyBox';

const FRModalContent = ({
  companiesWithMeasurementDateAndUsers,
  companiesWithMeasurementDateButNoUsers,
  companiesWithoutMeasurementDateButUsers,
  companiesWithoutMeasurementDateAndUsers,
  selectedCompanies,
  setSelectedCompanies,
  sendToAllCompanies,
  setSendToAllCompanies,
  allCompaniesSelected,
  setAllCompaniesSelected,
  companiesWithRequests,
}: FRModalContentProps) => {
  const selectableCompanies = useMemo(() => {
    if (sendToAllCompanies) {
      return companiesWithMeasurementDateAndUsers.concat(companiesWithMeasurementDateButNoUsers);
    }
    return companiesWithMeasurementDateAndUsers;
  }, [sendToAllCompanies, companiesWithMeasurementDateAndUsers, companiesWithMeasurementDateButNoUsers]);

  const handleSelectCompanies = useCallback(
    (company, status) => {
      if (status.selected) {
        const isAlreadySelected = selectedCompanies.find(selectedCompany => selectedCompany.id === company.id);
        // if is already selected, update onlyStaffEmails
        if (isAlreadySelected) {
          setSelectedCompanies(
            selectedCompanies.map(selectedCompany =>
              selectedCompany.id === company.id
                ? { ...selectedCompany, onlyStaffEmails: status.onlyStaffEmails }
                : selectedCompany
            )
          );
        } else {
          setSelectedCompanies([...selectedCompanies, { ...company, onlyStaffEmails: status.onlyStaffEmails }]);
        }
      } else {
        setSelectedCompanies(selectedCompanies.filter(selectedCompany => selectedCompany.id !== company.id));
      }
    },
    [selectedCompanies, setSelectedCompanies]
  );

  const toggleAllCompanies = () => {
    if (allCompaniesSelected) {
      setSelectedCompanies([]);
      setAllCompaniesSelected(false);
    } else {
      setSelectedCompanies(selectableCompanies);
      setAllCompaniesSelected(true);
    }
  };

  if (
    isEmpty(companiesWithMeasurementDateAndUsers)
    && isEmpty(companiesWithoutMeasurementDateButUsers)
    && isEmpty(companiesWithMeasurementDateButNoUsers)
    && isEmpty(companiesWithRequests)
    && isEmpty(companiesWithoutMeasurementDateAndUsers)
  ) {
    return <Typography>{NO_COMPANIES_FOUND}</Typography>;
  }

  return (
    <>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Tooltip title={CREATE_REQUESTS_TOOLTIP}>
          <Button
            onClick={() => setSendToAllCompanies(true)}
            variant={sendToAllCompanies ? 'contained' : 'outlined'}
            color="primary"
            style={{
              fontSize: '0.7rem',
            }}>
            {CREATE_REQUESTS}
          </Button>
        </Tooltip>
        <Tooltip title={SEND_REQUESTS_TOOLTIP}>
          <Button
            onClick={() => setSendToAllCompanies(false)}
            variant={!sendToAllCompanies ? 'contained' : 'outlined'}
            color="primary"
            style={{
              fontSize: '0.7rem',
            }}>
            {SEND_REQUESTS}
          </Button>
        </Tooltip>
      </div>
      {selectableCompanies.length > 1 && (
        <Button
          onClick={toggleAllCompanies}
          color="primary"
          style={{
            fontSize: '0.7rem',
          }}>
          {allCompaniesSelected ? UNSELECT_ALL_COMPANIES : SELECT_ALL_COMPANIES}
        </Button>
      )}
      <PerfectScrollbar
        style={{
          maxHeight: '20rem',
          overflow: 'auto',
          padding: '0.5rem 0',
        }}
        options={{
          suppressScrollX: true,
        }}>
        {selectableCompanies.map(company => (
          <ModalItemCompanyBox
            key={company.id}
            company={company}
            includeCheckbox
            handleSelectCompanies={handleSelectCompanies}
            selectedCompanies={selectedCompanies}
            displayCompanyUser={!sendToAllCompanies}
          />
        ))}
      </PerfectScrollbar>
      <InvalidCompaniesBox companies={companiesWithRequests} title={REQUEST_ALREADY_EXISTS} />
      <InvalidCompaniesBox companies={companiesWithoutMeasurementDateButUsers} title={NO_MEASUREMENT_DATE_SET} />
      {!sendToAllCompanies && (
        <InvalidCompaniesBox companies={companiesWithMeasurementDateButNoUsers} title={NO_COMPANY_USERS} />
      )}
      <InvalidCompaniesBox
        companies={companiesWithoutMeasurementDateAndUsers}
        title={`${NO_MEASUREMENT_DATE_SET} & ${NO_COMPANY_USERS}`}
      />
    </>
  );
};

export default FRModalContent;
